import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';

import { lastValueFrom } from 'rxjs';

import { MechanicsService } from "../services/mechanics.service"
import { SearchService } from "../services/search.service"
import { UtilsService } from "../services/utils.service"
import { UserService } from "../services/user.service"

import { decrypt, sortFamilyPatents } from '../utils';

import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { searchExamples } from './searchExamples';

@Component({
    selector: 'page-search',
    templateUrl: './page-search.component.html',
    styleUrls: ['./page-search.component.styl'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PageSearchComponent implements OnInit {

	public $searchTerms = new Subject<string>()

	searchTerms: string = ""

	timeoutDebounceSearch: any = null;

	resultsTableCols: any[] = [
		{ label: "INN", target: "inns" },
		{ label: "Participant", target: "" },
		{ label: "Patents", target: "" }
	]

	searchExample: string

	// https://www.npmjs.com/package/ng-multiselect-dropdown
	dropdownSettingsCountries: IDropdownSettings = {
		singleSelection: false,
		idField: "cc",   // default: "id"
		textField: "label", // default: "text"
		selectAllText: 'Select All',
		unSelectAllText: 'Unselect All',
		itemsShowLimit: 1,
		allowSearchFilter: true,
		maxHeight: 500,
		enableCheckAll: false
	};

	constructor(public ms: MechanicsService,
		public ss: SearchService,
		public utils: UtilsService,
		public us: UserService,
		private cdr: ChangeDetectorRef) {

		this.searchExample = searchExamples[Math.floor(Math.random() * searchExamples.length)]

	}

	ngOnInit(): void {
		this.$searchTerms
			.pipe(
				debounceTime(300)
			)
			.subscribe(term => this.ss.search(term));
	}

	onInput($event?: KeyboardEvent): void {

		const l = `pageSearch onInput - `

		// console.log(`${l}$event = `, $event)

		if ($event) {

			$event = $event as KeyboardEvent

			let value = $event.target["value"] || "";

			this.searchTerms = value.trim();
			this.$searchTerms.next(value)

		} else {

			console.log(`${l}onInput() - no $event`);

			this.$searchTerms.next("");
		}
	}

	onInnClick(innName: string) {

		innName = innName.replace(/ \(\d+\)$/, "") // Removing the count at the end

		this.searchTerms = innName;
		this.$searchTerms.next(innName);
	}

	async getOneFamily(_id: string) { // similar to mas.selectFamily

		const l = `page-search getOneFamily() - `

		this.ms.isLoading = true;

		let data: string;

		const countries: string[] = this.ss.selectedCountries.map(obj => obj.cc)

		try {

			data = await lastValueFrom(this.utils.getOneFamily({
				_id,
				cc: countries
			})); // also used in mas

		} catch (err) {

			if (err.message.includes("failure during parsing")) {
				// We are probably in dev, the result is in plain JSON instead of encoded text.
				// console.log(`Looks like the response is plain JSON instead of encoded text, trying to handle the response anyway`)

			} else {

				console.error(`${l}Search error`, err)
				this.ms.isLoading = false;
				return;
			}
		}

		// console.log(`${l}got data = `, data)

		let fullFamily;
		try {
			// console.log(`${l}Decryping...`);
			fullFamily = decrypt(data); // encrypted, NODE_ENV==="production"
			// console.log(`${l}Decrypted OK`);
		} catch (err) {
			// console.log(`${l}Error decrypting data: `, err);
			fullFamily = JSON.parse(data) // JSON, NODE_ENV==='development" - But still arrives as a string since we specified { responseType: "text" }
		}

		// console.log(`${l}fullFamily = `,fullFamily)

		// Ensure title and abstract arrays exist
		if (!fullFamily.title || !Array.isArray(fullFamily.title) || fullFamily.title.length === 0) {
			fullFamily.title = [];
		}
		if (!fullFamily.abstract || !Array.isArray(fullFamily.abstract) || fullFamily.abstract.length === 0) {
			fullFamily.abstract = [];
		}

		this.ms.familyLanguages = (fullFamily.title || []).map(obj => ({
			lang: obj["lang"] || "ol",
			source: obj["cc"] || "?"
		}));

		// Ensure we have at least one language entry
		if (this.ms.familyLanguages.length === 0) {
			this.ms.familyLanguages = [{ lang: "en", source: "?" }];
		}

		// Initialize selectedLanguageIndex
		if (this.ms.patentsFilters?.preferredLanguage) {
			const langIndex = this.ms.familyLanguages.findIndex(obj => obj.lang === this.ms.patentsFilters.preferredLanguage);
			this.ms.selectedLanguageIndex = langIndex !== -1 ? langIndex : 0;
		} else {
			this.ms.selectedLanguageIndex = 0;
		}

		// console.log(`${l}ms.familyLanguages = `, this.ms.familyLanguages)

		fullFamily = this.utils.computeFamilyCustomKeys(fullFamily)
		this.ms.selectedFamily = sortFamilyPatents(fullFamily)
		window["selectedFamily"] = this.ms.selectedFamily
		this.ms.isLoading = false;

		// Defer the visual state change until after the current change detection cycle.
		setTimeout(() => {
			this.ms.condensedSearchContainer = true
			this.cdr.detectChanges();
			this.setPatentsSize();
		}, 0);
	}

	async setPatentsSize() {

		const l = `setPatentsSize() - `

		if (!this.ms.selectedFamily) return

		await new Promise(r => setTimeout(r)); // Waiting for Angular to render

		const ulResults = document.querySelector("ul.results");

		if (!ulResults) return;

		const $allLi = Array.from(ulResults.querySelectorAll("li"));

		let tallestPx: number = Math.max(
			...Array.from($allLi)
				.map(($li: HTMLLIElement) => $li.offsetHeight)
		)

		// console.log(`${l}tallestPx = `, tallestPx);

		$allLi.forEach(($li: HTMLLIElement) => {
			$li.style.height = `${tallestPx}px`
		});
	}

	onCountrySelect($event, action: string): void {

		const l = `onCountrySelect() - `

		// console.log(`${l}$event = `, $event) // $event =  {cc: 'AL', label: 'AL - Albania'}

		this.$searchTerms.next(this.searchTerms);
	}

	refreshMultiselectView(): void {
		setTimeout(() => this.cdr.detectChanges(), 0);
	}

}
